import React, { useEffect, useState } from 'react';
import {
    Typography,
    Container,
    Box,
    TextField,
    InputAdornment,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    Avatar,
    ListItemText,
    Divider,
    Paper,
    Stack,
    Button,
    createTheme,
    ThemeProvider,
    CssBaseline,
    Tabs,
    Tab,
    Badge,
    CircularProgress,
    Skeleton
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { get, post, put, del } from '../utility/fetch';
import { get as gets } from '../utility/fetchWeb';

import notification from '../utility/notification';
import { Cancel, CancelRounded } from '@mui/icons-material';
import axios from 'axios';

const theme = createTheme({
    palette: {
        primary: {
            main: '#28a745',
        },
        secondary: {
            main: '#f8f9fa',
        },
        text: {
            primary: '#333',
            secondary: '#6c757d',
        },
    },
    typography: {
        fontFamily: 'Roboto, sans-serif',
        h4: {
            fontWeight: 600,
            fontSize: '2rem',
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        body1: {
            fontSize: '1rem',
            '@media (max-width:600px)': {
                fontSize: '0.9rem',
            },
        },
        caption: {
            fontSize: '0.8rem',
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '25px',
                    paddingLeft: '18px',
                    paddingRight: '18px',
                },
                containedPrimary: {
                    backgroundColor: '#28a745',
                    '&:hover': {
                        backgroundColor: '#218838',
                    }
                },
                outlined: {
                    borderColor: '#ced4da',
                    color: '#6c757d',
                    '&:hover': {
                        borderColor: '#adb5bd',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    }
                }
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    border: '4px solid white',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#6c757d',
                    '&.Mui-selected': {
                        color: '#28a745',
                    },
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: '#28a745',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: '#e0e0e0',
                    color: '#333',
                    borderRadius: '8px',
                    fontWeight: 500,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    borderRadius: '25px',
                    backgroundColor: '#f5f5f5',
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#e0e0e0',
                },
                root: {
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#adb5bd',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28a745',
                    },
                },
            },
        },
    },
});

const LoadingSkeleton = () => (
    <>
        {[1, 2, 3].map((i) => (
            <React.Fragment key={i}>
                <ListItem sx={{ py: 1.5, px: 3 }}>
                    <ListItemAvatar>
                        <Skeleton variant="circular" width={56} height={56} />
                    </ListItemAvatar>
                    <ListItemText
                        primary={<Skeleton width="60%" />}
                        secondary={
                            <Stack spacing={0.5}>
                                <Skeleton width="40%" />
                                <Skeleton width="30%" />
                            </Stack>
                        }
                    />
                    <Skeleton width={100} height={36} sx={{ borderRadius: 25 }} />
                </ListItem>
                <Divider />
            </React.Fragment>
        ))}
    </>
);

function FriendRequests() {
    const [activeTab, setActiveTab] = useState(0);
    const [patients, setPatients] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [receivedRequests, setReceivedRequests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    // Add new state for employees
    const [employees, setEmployees] = useState([]);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);
    const [isLoadingSent, setIsLoadingSent] = useState(false);
    const [isLoadingReceived, setIsLoadingReceived] = useState(false);
    const [isActionLoading, setIsActionLoading] = useState(false); // Loading state for actions
    const [searchQuery, setSearchQuery] = useState('');
    const [loadingButtonIds, setLoadingButtonIds] = useState([]); // Track loading state per button

    useEffect(() => {
        if (searchQuery.length < 3) {
            setPatients([]);
            setIsLoadingSearch(false);
            return;
        }

        const fetchPatients = async () => {
            setIsLoadingSearch(true);
            try {
                const res = await get(
                    `/Patient/filter-list/PatientName/${searchQuery}/1/10`
                );
                if (res?.statusCode === 200) {
                    setPatients(res.data.recordList || []);
                } else {
                    setPatients([]);
                }
            } catch (err) {
                console.error('Fetch patients error', err);
                setPatients([]);
            } finally {
                setIsLoadingSearch(false);
            }
        };

        fetchPatients();
    }, [searchQuery]);

   

    const getSentRequests = async () => {
        setIsLoadingSent(true);
        try {
            const userId = sessionStorage.getItem('userId');
            const res = await get(`/friendrequest/list/employee/${userId}`);
            if (res?.data) {
                setSentRequests(res?.data);
            }
        } catch (error) {
            console.error('Error fetching sent requests:', error);
        } finally {
            setIsLoadingSent(false);
        }
    };

    const getReceivedRequests = async () => {
        setIsLoadingReceived(true);
        try {
            const userId = sessionStorage.getItem('userId');
            const res = await get(`/friendrequest/list/receiver-employee/${userId}`);
            if (res?.data) {
                setReceivedRequests(res.data);
            }
        } catch (error) {
            console.error('Error fetching received requests:', error);
        } finally {
            setIsLoadingReceived(false);
        }
    };

    // Add function to get employees
    const getEmployees = async () => {
        setIsLoadingSearch(true);
        try {
            const res = await get('/employee/list/1/10000');
            if (res?.statusCode === 200) {
                setEmployees(res?.data?.recordList);
            }
        } catch (error) {
            setEmployees([]);
            console.error('Error fetching employees:', error);
        } finally {
            setIsLoadingSearch(false);
        }
    };

    const handleSendRequest = async (id, isHealthPractitioner) => {
        setLoadingButtonIds(prev => [...prev, id]); // Add this button to loading state
        const userId = Number(sessionStorage.getItem('userId'));
        const isSenderHealthPractitioner = true;

        const headers = {
            Accept: 'application/json',
        };

        const token = sessionStorage.getItem('token');
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        try {
            const payload = {
                senderPatientId: isSenderHealthPractitioner ? 0 : userId,
                senderEmployeeId: isSenderHealthPractitioner ? userId : 0,
                isSenderHealthPractitioner: true,
                friendRequests: [
                    {
                        receiverPatientId: isHealthPractitioner ? 0 : id,
                        receiverEmployeeId: isHealthPractitioner ? id : 0,
                        isReceiverHealthPractitioner: isHealthPractitioner,
                    },
                ],
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/friendrequest/create-friend-request`,
                payload,
                { headers }
            );

            if (response?.data?.statusCode === 200) {
                notification({
                    type: 'success',
                    message: 'Friend request sent successfully',
                });
            }
        } catch (error) {
            const errorMessage =
                error.response?.data?.errorData?.[0] ||
                error.response?.data?.message ||
                'Failed to send friend request';
            notification({
                type: 'error',
                message: errorMessage,
            });
        } finally {
            setLoadingButtonIds(prev => prev.filter(buttonId => buttonId !== id)); // Remove this button from loading state
            getSentRequests(); // Refresh sent requests after sending a new one
            getReceivedRequests();
        }
    };

    const handleCancelRequest = async (requestId) => {
        setIsActionLoading(true); // Set loading state
        try {
            await del(`/friendrequest/${requestId}`);
            notification({
                type: 'success',
                message: 'Request cancelled successfully',
            });
            getSentRequests();
        } catch (error) {
            notification({
                type: 'error',
                message: 'Failed to cancel request',
            });
        } finally {
            setIsActionLoading(false); // Reset loading state
        }
    };

    const handleRespondToRequest = async (requestId, isAccepted) => {
        setIsActionLoading(true); // Set loading state
        try {
            const userId = sessionStorage.getItem('userId');
            const res = await put(`/friendrequest/${requestId}/employee/${userId}/respond-to-request`, {
                isAccepted,
            });
            console.log('Response to request:', res);
            if (res?.statusCode === 200) {
                notification({
                    type: 'success',
                    message: isAccepted ? 'Friend request accepted' : 'Friend request declined',
                });
                getReceivedRequests();
            }
        } catch (error) {
            notification({
                type: 'error',
                message: 'Failed to respond to request',
            });
        } finally {
            setIsActionLoading(false); // Reset loading state
        }
    };

    // Add this helper function at the top of your component
    const isHealthPractitioner = (person) => {
        if (person.userRoles) {
            // Check employee's roles for doctor/health practitioner role
            return person.userRoles.some(role =>
                role.role.name.toLowerCase().includes('doctor') ||
                role.role.name.toLowerCase().includes('nurse') ||
                role.role.name.toLowerCase().includes('health')
            );
        }
        // If it's a patient (has usersType field)
        return false;
    };

    // Add this helper function to get user type display text
    const getUserTypeDisplay = (person) => {
        if (person.userRoles) {
            // For employees, show their role
            const role = person.userRoles.find(role =>
                role.role.name.toLowerCase().includes('doctor') ||
                role.role.name.toLowerCase().includes('nurse') ||
                role.role.name.toLowerCase().includes('health')
            );
            return role ? role.role.name : 'Staff';
        }
        // For patients
        return 'Citizen';
    };

    // Add this helper function to get receiver details
    const getReceiverDetails = (request) => {
        if (request?.employeeReceiver?.id !== 0) {
            return {
                firstName: request.employeeReceiver.firstName,
                lastName: request.employeeReceiver.lastName,
                pictureUrl: request.employeeReceiver.pictureUrl
            };
        }
        return {
            firstName: request.patientReceiver.firstName,
            lastName: request.patientReceiver.lastName,
            pictureUrl: request.patientReceiver.pictureUrl
        };
    };

    useEffect(() => {
        getEmployees();
        getSentRequests();
        getReceivedRequests();
    }, [activeTab]);

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ backgroundColor: theme.palette.background.default, minHeight: '100vh', py: 4 }}>
                <Container maxWidth="md">
                    <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3 }}>
                        Friend Requests
                    </Typography>

                    <Tabs
                        value={activeTab}
                        onChange={(e, newValue) => setActiveTab(newValue)}
                        sx={{
                            mb: 3,
                            '& .MuiTab-root': {
                                minWidth: 150, // Increase minimum width of tabs
                                fontSize: '0.95rem',
                                px: 3, // Add horizontal padding
                            }
                        }}
                    >
                        <Tab
                            label="Find Friends"
                            sx={{ minWidth: 120 }} // First tab can be smaller
                        />
                        <Tab
                            label={
                                <Badge
                                    badgeContent={sentRequests?.length || 0}
                                    color="primary"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            right: -10, // Adjust badge position
                                            top: -4,
                                        }
                                    }}
                                >
                                    Sent Requests
                                </Badge>
                            }
                        />
                        <Tab
                            label={
                                <Badge
                                    badgeContent={
                                        receivedRequests?.filter(req => req.status === 0)?.length || 0
                                    }
                                    color="error"
                                    sx={{
                                        '& .MuiBadge-badge': {
                                            right: -10, // Adjust badge position
                                            top: -4,
                                        }
                                    }}
                                >
                                    Received Requests
                                </Badge>
                            }
                        />
                    </Tabs>

                    {activeTab === 0 && (
                        <>
                            <TextField
                                variant="outlined"
                                placeholder="Search for people"
                                fullWidth
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto', maxHeight: '70vh' }}>
                                <List disablePadding>
                                    {isLoadingSearch ? (
                                        <LoadingSkeleton />
                                    ) : (
                                        [...patients, ...employees]
                                            ?.filter(person =>
                                                `${person?.firstName} ${person?.lastName}`
                                                    .toLowerCase()
                                                    .includes(searchTerm.toLowerCase())
                                            )
                                            .map((person, index, arr) => (
                                                <React.Fragment key={`${person?.id}-${person?.userRoles ? 'employee' : 'patient'}`}>
                                                    <ListItem
                                                        sx={{
                                                            py: 1.5,
                                                            px: 3,
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                            },
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar
                                                                src={person?.pictureUrl || person?.profilePicture}
                                                                sx={{ width: 56, height: 56 }}
                                                            >
                                                                {person?.firstName?.[0]}{person?.lastName?.[0]}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={`${person?.firstName} ${person?.lastName}`}
                                                            secondary={
                                                                <Stack spacing={0.5}>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {person?.email}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: isHealthPractitioner(person) ? 'primary.main' : 'text.secondary',
                                                                            fontWeight: isHealthPractitioner(person) ? 600 : 400
                                                                        }}
                                                                    >
                                                                        {getUserTypeDisplay(person)}
                                                                        {person?.healthCareProvider && ` • ${person.healthCareProvider.name}`}
                                                                    </Typography>
                                                                </Stack>
                                                            }
                                                        />
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<PersonAddIcon />}
                                                            onClick={() => handleSendRequest(person?.id, isHealthPractitioner(person))}
                                                            disabled={
                                                                loadingButtonIds.includes(person?.id) ||
                                                                person?.id === Number(sessionStorage.getItem("userId")) ||
                                                                person?.followList?.includes(parseInt(sessionStorage.getItem("userId")))
                                                            }
                                                        >
                                                            {person?.followList?.includes(parseInt(sessionStorage.getItem("userId"))) ? 'Following' : 'Follow'}
                                                        </Button>
                                                    </ListItem>
                                                    {index < arr.length - 1 && <Divider />}
                                                </React.Fragment>
                                            )))}
                                </List>
                            </Paper>
                        </>
                    )}

                    {activeTab === 1 && (
                        <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto', maxHeight: '70vh' }}>
                            <List disablePadding>
                                {isLoadingSent ? (
                                    <LoadingSkeleton />
                                ) : sentRequests?.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No requests sent
                                        </Typography>
                                    </Box>
                                ) : (
                                    sentRequests?.map((request, index) => {
                                        const receiver = getReceiverDetails(request);
                                        return (
                                            <React.Fragment key={request?.id}>
                                                <ListItem
                                                    sx={{
                                                        py: 1.5,
                                                        px: 3,
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            src={receiver?.pictureUrl}
                                                            sx={{ width: 56, height: 56 }}
                                                        >
                                                            {receiver?.firstName?.[0]}
                                                            {receiver?.lastName?.[0]}
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={`${receiver?.firstName} ${receiver?.lastName}`}
                                                        secondary={
                                                            <>
                                                                Sent on {new Date(request?.createdAt).toLocaleDateString()}
                                                                {request?.status === 2 && (
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            ml: 1,
                                                                            color: 'success.main',
                                                                            fontSize: '0.875rem'
                                                                        }}
                                                                    >
                                                                        • Accepted on {new Date(request?.acceptedAt).toLocaleDateString()}
                                                                    </Typography>
                                                                )}
                                                                {request?.status === 3 && (
                                                                    <Typography
                                                                        component="span"
                                                                        sx={{
                                                                            ml: 1,
                                                                            color: 'error.main',
                                                                            fontSize: '0.875rem'
                                                                        }}
                                                                    >
                                                                        • Rejected on {new Date(request?.rejectedAt).toLocaleDateString()}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        }
                                                    />
                                                    {request?.status === 0 && (
                                                        <Stack direction="row" spacing={1}>
                                                            <Button
                                                                variant="contained"
                                                                color="error"
                                                                startIcon={<CancelRounded />}
                                                                onClick={() => handleCancelRequest(request?.id)}
                                                                disabled={isActionLoading} // Disable button during loading
                                                            >
                                                                Cancel Request
                                                            </Button>
                                                        </Stack>
                                                    )}

                                                </ListItem>
                                                {index < sentRequests?.length - 1 && <Divider />}
                                            </React.Fragment>
                                        );
                                    }))}
                            </List>
                        </Paper>
                    )}

                    {activeTab === 2 && (
                        <Paper elevation={0} sx={{ borderRadius: '16px', overflow: 'auto', maxHeight: '70vh' }}>
                            <List disablePadding>
                                {isLoadingReceived ? (
                                    <LoadingSkeleton />
                                ) : receivedRequests?.length === 0 ? (
                                    <Box sx={{ p: 4, textAlign: 'center' }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No pending requests
                                        </Typography>
                                    </Box>
                                ) : (
                                    receivedRequests?.map((request, index) => (
                                        <React.Fragment key={request?.id}>
                                            <ListItem sx={{ py: 1.5, px: 3 }}>
                                                <ListItemAvatar>
                                                    <Avatar
                                                        src={request?.employeeSender?.pictureUrl || request?.patientSender?.pictureUrl || ''}
                                                        sx={{ width: 56, height: 56 }}
                                                    >
                                                        {request?.employeeSender?.firstName?.[0] || request?.patientSender?.firstName?.[0] || ''}
                                                        {request?.employeeSender?.lastName?.[0] || request?.patientSender?.lastName?.[0] || ''}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={`${request?.employeeSender?.firstName || request?.patientSender?.firstName || ''} 
                                                              ${request?.employeeSender?.lastName || request?.patientSender?.lastName || ''}`}
                                                    secondary={`Received on ${request?.createdAt ? new Date(request?.createdAt).toLocaleDateString() : 'Unknown Date'}`}
                                                />
                                                <Stack direction="row" spacing={1}>
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        startIcon={<CheckIcon />}
                                                        onClick={() => handleRespondToRequest(request?.id, true)}
                                                        disabled={isActionLoading}
                                                    >
                                                        Accept
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        startIcon={<CloseIcon />}
                                                        onClick={() => handleRespondToRequest(request?.id, false)}
                                                        disabled={isActionLoading}
                                                    >
                                                        Decline
                                                    </Button>
                                                </Stack>
                                            </ListItem>
                                            {index < receivedRequests?.length - 1 && <Divider />}
                                        </React.Fragment>
                                    )))}
                            </List>
                        </Paper>
                    )}
                </Container>
            </Box>
        </ThemeProvider>
    );
}

export default FriendRequests;